// ─────────────────────────────────────────────────────────────────────────────
// Customers.js
// Contractor customers management page.
//
// FIX: CustomerForm was previously a component defined inside Customers,
// causing it to remount on every state change and lose input focus after
// one character. Form JSX is now inlined directly into each modal.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import Sidebar from '../components/Sidebar'
import TrialBanner from '../components/TrialBanner'

export default function Customers({ session }) {
  // ── State ──────────────────────────────────────────────────────────────────
  const [customers, setCustomers] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const emptyForm = {
    customer_name: '',
    customer_type: 'Business',
    email: '',
    phone: '',
    address: '',
    vat_number: '',
    site_addresses: [],
  }

  const [form, setForm] = useState(emptyForm)

  // ── Data fetching ──────────────────────────────────────────────────────────
  useEffect(() => { fetchData() }, [])

  const fetchData = async () => {
    const { data: profile } = await supabase
      .from('profiles')
      .select('company_id')
      .eq('id', session.user.id)
      .single()

    if (profile?.company_id) {
      const { data } = await supabase
        .from('customers')
        .select('*')
        .eq('company_id', profile.company_id)
        .order('created_at', { ascending: false })
      setCustomers(data || [])
    }
    setLoading(false)
  }

  const getCompanyId = async () => {
    const { data: profile } = await supabase
      .from('profiles')
      .select('company_id')
      .eq('id', session.user.id)
      .single()
    return profile?.company_id
  }

  const resetForm = () => {
    setForm(emptyForm)
    setError(null)
  }

  // ── CRUD handlers ──────────────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!form.customer_name.trim()) { setError('Customer name is required.'); return }
    setSaving(true)
    setError(null)

    const companyId = await getCompanyId()
    const { error } = await supabase.from('customers').insert([{
      customer_name: form.customer_name.trim(),
      customer_type: form.customer_type,
      email: form.email.trim(),
      phone: form.phone.trim(),
      address: form.address.trim(),
      vat_number: form.customer_type === 'Business' ? form.vat_number.trim() : null,
      site_addresses: form.customer_type === 'Business' ? form.site_addresses.filter(a => a.trim()) : [],
      company_id: companyId,
    }])

    if (error) setError(error.message)
    else { setShowAddModal(false); resetForm(); fetchData() }
    setSaving(false)
  }

  const handleEdit = async () => {
    if (!form.customer_name.trim()) { setError('Customer name is required.'); return }
    setSaving(true)
    setError(null)

    const { error } = await supabase.from('customers').update({
      customer_name: form.customer_name.trim(),
      customer_type: form.customer_type,
      email: form.email.trim(),
      phone: form.phone.trim(),
      address: form.address.trim(),
      vat_number: form.customer_type === 'Business' ? form.vat_number.trim() : null,
      site_addresses: form.customer_type === 'Business' ? form.site_addresses.filter(a => a.trim()) : [],
    }).eq('id', selectedCustomer.id)

    if (error) setError(error.message)
    else { setShowEditModal(false); resetForm(); fetchData() }
    setSaving(false)
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return
    await supabase.from('customers').delete().eq('id', id)
    fetchData()
  }

  const openEdit = (customer) => {
    setSelectedCustomer(customer)
    setForm({
      customer_name: customer.customer_name || '',
      customer_type: customer.customer_type || 'Business',
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.address || '',
      vat_number: customer.vat_number || '',
      site_addresses: customer.site_addresses || [],
    })
    setError(null)
    setShowEditModal(true)
  }

  const openView = (customer) => {
    setSelectedCustomer(customer)
    setShowViewModal(true)
  }

  // ── Site address helpers ───────────────────────────────────────────────────
  const addSiteAddress = () => {
    if (form.site_addresses.length >= 10) return
    setForm(f => ({ ...f, site_addresses: [...f.site_addresses, ''] }))
  }

  const updateSiteAddress = (index, value) => {
    setForm(f => {
      const updated = [...f.site_addresses]
      updated[index] = value
      return { ...f, site_addresses: updated }
    })
  }

  const removeSiteAddress = (index) => {
    setForm(f => ({ ...f, site_addresses: f.site_addresses.filter((_, i) => i !== index) }))
  }

  // ── Styles ─────────────────────────────────────────────────────────────────
  const typeBadge = (type) => ({
    padding: '3px 10px', borderRadius: '999px',
    fontSize: '12px', fontWeight: '600', display: 'inline-block',
    background: type === 'Business' ? '#EFF6FF' : '#F0FDF4',
    color: type === 'Business' ? '#2563EB' : '#15803D',
    whiteSpace: 'nowrap', width: 'fit-content',
  })

  const inputStyle = {
    width: '100%', height: '44px', borderRadius: '10px',
    border: '1.5px solid #E2E8F0', padding: '0 14px',
    fontSize: '14px', outline: 'none', boxSizing: 'border-box',
    background: '#FAFAFA', fontFamily: 'Inter, sans-serif',
  }

  const labelStyle = {
    display: 'block', fontSize: '13px', fontWeight: '600',
    color: '#374151', marginBottom: '6px',
  }

  // ── Inline form JSX ────────────────────────────────────────────────────────
  // IMPORTANT: This is NOT a component — it is inlined directly into each modal.
  // Do NOT extract this into a separate CustomerForm component defined inside
  // this function — doing so causes the form to remount on every keystroke,
  // losing input focus after a single character.
  const formJsx = (
    <div>
      {error && (
        <div style={{
          background: '#FFF1F2', border: '1px solid #FECDD3',
          borderRadius: '8px', padding: '12px',
          color: '#BE123C', fontSize: '13px', marginBottom: '16px',
        }}>{error}</div>
      )}

      {/* Customer Type */}
      <div style={{ marginBottom: '20px' }}>
        <label style={labelStyle}>Customer Type</label>
        <div style={{ display: 'flex', gap: '24px' }}>
          {['Business', 'Private'].map(type => (
            <label key={type} style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              cursor: 'pointer', fontSize: '14px', color: '#374151',
              fontWeight: form.customer_type === type ? '600' : '400',
            }}>
              <div
                onClick={() => setForm(f => ({ ...f, customer_type: type }))}
                style={{
                  width: '18px', height: '18px', borderRadius: '4px',
                  border: `2px solid ${form.customer_type === type ? '#2563EB' : '#E2E8F0'}`,
                  background: form.customer_type === type ? '#2563EB' : 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', flexShrink: 0,
                }}
              >
                {form.customer_type === type && (
                  <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                    <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                )}
              </div>
              {type}
            </label>
          ))}
        </div>
      </div>

      {/* Name */}
      <div style={{ marginBottom: '16px' }}>
        <label style={labelStyle}>
          {form.customer_type === 'Business' ? 'Business Name *' : 'Full Name *'}
        </label>
        <input
          type="text"
          value={form.customer_name}
          onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))}
          placeholder={form.customer_type === 'Business' ? 'e.g. ABC Plumbing Ltd' : 'e.g. John Smith'}
          style={inputStyle}
        />
      </div>

      {/* Email & Phone */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
        <div>
          <label style={labelStyle}>Email</label>
          <input
            type="email"
            value={form.email}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
            placeholder="email@example.com"
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>Phone</label>
          <input
            type="text"
            value={form.phone}
            onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
            placeholder="+44 7700 000000"
            style={inputStyle}
          />
        </div>
      </div>

      {/* Address */}
      <div style={{ marginBottom: '16px' }}>
        <label style={labelStyle}>
          {form.customer_type === 'Business' ? 'Billing / Main Address' : 'Address'}
        </label>
        <input
          type="text"
          value={form.address}
          onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
          placeholder="123 Main Street, London, EC1A 1BB"
          style={inputStyle}
        />
      </div>

      {/* Business only fields */}
      {form.customer_type === 'Business' && (
        <>
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>VAT Number</label>
            <input
              type="text"
              value={form.vat_number}
              onChange={e => setForm(f => ({ ...f, vat_number: e.target.value }))}
              placeholder="GB123456789"
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label style={{ ...labelStyle, margin: 0 }}>
                Site Addresses
                <span style={{ fontSize: '12px', color: '#94A3B8', fontWeight: '400', marginLeft: '8px' }}>(up to 10)</span>
              </label>
              {form.site_addresses.length < 10 && (
                <button onClick={addSiteAddress} style={{
                  padding: '4px 12px', borderRadius: '6px', fontSize: '12px',
                  fontWeight: '600', border: '1.5px solid #2563EB',
                  background: 'white', color: '#2563EB', cursor: 'pointer',
                }}>+ Add Site</button>
              )}
            </div>

            {form.site_addresses.length === 0 ? (
              <p style={{ fontSize: '13px', color: '#94A3B8', margin: '0 0 8px' }}>
                No site addresses added yet.
              </p>
            ) : (
              form.site_addresses.map((addr, index) => (
                <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                  <div style={{
                    width: '24px', height: '24px', borderRadius: '50%',
                    background: '#EFF6FF', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontSize: '11px', fontWeight: '700',
                    color: '#2563EB', flexShrink: 0,
                  }}>{index + 1}</div>
                  <input
                    type="text"
                    value={addr}
                    onChange={e => updateSiteAddress(index, e.target.value)}
                    placeholder={`Site address ${index + 1}`}
                    style={{ ...inputStyle, height: '40px', flex: 1 }}
                  />
                  <button onClick={() => removeSiteAddress(index)} style={{
                    width: '32px', height: '32px', borderRadius: '6px',
                    border: '1.5px solid #FECDD3', background: 'white',
                    color: '#BE123C', cursor: 'pointer', fontSize: '16px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>×</button>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  )

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{
      display: 'flex', minHeight: '100vh',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
      background: '#F7F8FC',
    }}>
      <Sidebar session={session} />
     

      <div style={{ marginLeft: '240px', flex: 1, padding: '36px 40px' }}>

        {/* ── Page Header ── */}
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-start', marginBottom: '28px',
        }}>
          <div>
            <h1 style={{ fontSize: '22px', fontWeight: '800', color: '#0F172A', margin: '0 0 4px', letterSpacing: '-0.4px' }}>
              Customers
            </h1>
            <p style={{ fontSize: '13px', color: '#94A3B8', margin: 0 }}>
              {customers.length} total customers
            </p>
          </div>
          <button onClick={() => { resetForm(); setShowAddModal(true) }} style={{
            height: '40px', padding: '0 20px', borderRadius: '10px',
            background: 'linear-gradient(135deg, #2563EB, #1D4ED8)',
            color: 'white', fontSize: '13px', fontWeight: '700',
            border: 'none', cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(37,99,235,0.30)',
          }}>+ Add Customer</button>
        </div>

        <TrialBanner session={session} />

        {/* ── Customers Table ── */}
        <div style={{
          background: 'white', borderRadius: '12px',
          border: '1px solid #EAECF0', overflow: 'hidden',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 100px 180px 140px 170px',
            padding: '10px 24px', background: '#F8FAFC',
            borderBottom: '1px solid #EAECF0',
          }}>
            {['Customer Name', 'Type', 'Email', 'Phone', 'Actions'].map((h, i) => (
              <span key={i} style={{
                fontSize: '11px', fontWeight: '600', color: '#94A3B8',
                textTransform: 'uppercase', letterSpacing: '0.5px',
              }}>{h}</span>
            ))}
          </div>

          {loading ? (
            <div style={{ padding: '48px', textAlign: 'center', color: '#94A3B8' }}>Loading...</div>
          ) : customers.length === 0 ? (
            <div style={{ padding: '64px', textAlign: 'center' }}>
              <p style={{ fontSize: '15px', fontWeight: '700', color: '#0F172A', margin: '0 0 6px' }}>No customers yet</p>
              <p style={{ fontSize: '13px', color: '#94A3B8', margin: '0 0 20px' }}>Add your first customer to get started</p>
              <button onClick={() => { resetForm(); setShowAddModal(true) }} style={{
                padding: '8px 20px', borderRadius: '8px',
                background: 'linear-gradient(135deg, #2563EB, #1D4ED8)',
                color: 'white', fontSize: '13px', fontWeight: '600',
                border: 'none', cursor: 'pointer',
              }}>+ Add Customer</button>
            </div>
          ) : (
            customers.map((customer, i) => (
              <div key={customer.id} style={{
                display: 'grid',
                gridTemplateColumns: '1fr 100px 180px 140px 170px',
                padding: '14px 24px', alignItems: 'center',
                borderBottom: i < customers.length - 1 ? '1px solid #F1F5F9' : 'none',
              }}
                onMouseEnter={e => e.currentTarget.style.background = '#FAFBFC'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div>
                  <p style={{ fontSize: '13px', fontWeight: '600', color: '#0F172A', margin: '0 0 2px' }}>
                    {customer.customer_name}
                  </p>
                  {customer.customer_type === 'Business' && customer.vat_number && (
                    <p style={{ fontSize: '11px', color: '#94A3B8', margin: 0 }}>VAT: {customer.vat_number}</p>
                  )}
                  {customer.customer_type === 'Business' && customer.site_addresses?.length > 0 && (
                    <p style={{ fontSize: '11px', color: '#94A3B8', margin: 0 }}>
                      {customer.site_addresses.length} site{customer.site_addresses.length > 1 ? 's' : ''}
                    </p>
                  )}
                </div>
                <span style={typeBadge(customer.customer_type)}>{customer.customer_type || 'Business'}</span>
                <span style={{ fontSize: '12px', color: '#64748B' }}>{customer.email || '—'}</span>
                <span style={{ fontSize: '12px', color: '#64748B' }}>{customer.phone || '—'}</span>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button onClick={() => openView(customer)} style={{
                    padding: '5px 10px', borderRadius: '6px', fontSize: '12px',
                    fontWeight: '600', border: '1.5px solid #E2E8F0',
                    background: 'white', color: '#374151', cursor: 'pointer',
                  }}>View</button>
                  <button onClick={() => openEdit(customer)} style={{
                    padding: '5px 10px', borderRadius: '6px', fontSize: '12px',
                    fontWeight: '600', border: '1.5px solid #E2E8F0',
                    background: 'white', color: '#2563EB', cursor: 'pointer',
                  }}>Edit</button>
                  <button onClick={() => handleDelete(customer.id)} style={{
                    padding: '5px 10px', borderRadius: '6px', fontSize: '12px',
                    fontWeight: '600', border: '1.5px solid #FECDD3',
                    background: 'white', color: '#BE123C', cursor: 'pointer',
                  }}>Delete</button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* ── Add Customer Modal ── */}
      {showAddModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 100, padding: '24px',
        }}>
          <div style={{
            background: 'white', borderRadius: '16px', padding: '32px',
            width: '100%', maxWidth: '540px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            maxHeight: '90vh', overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '800', color: '#0F172A', margin: 0 }}>Add Customer</h2>
              <div onClick={() => { setShowAddModal(false); resetForm() }} style={{
                width: '32px', height: '32px', borderRadius: '8px', background: '#F1F5F9',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', fontSize: '16px', color: '#64748B',
              }}>×</div>
            </div>
            {formJsx}
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button onClick={() => { setShowAddModal(false); resetForm() }} style={{
                flex: 1, height: '44px', borderRadius: '10px',
                background: 'white', border: '1.5px solid #E2E8F0',
                fontSize: '14px', fontWeight: '600', color: '#64748B', cursor: 'pointer',
              }}>Cancel</button>
              <button onClick={handleCreate} disabled={saving} style={{
                flex: 1, height: '44px', borderRadius: '10px',
                background: 'linear-gradient(135deg, #2563EB, #1D4ED8)',
                color: 'white', fontSize: '14px', fontWeight: '700',
                border: 'none', cursor: saving ? 'not-allowed' : 'pointer',
              }}>{saving ? 'Saving...' : 'Add Customer'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Customer Modal ── */}
      {showEditModal && selectedCustomer && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 100, padding: '24px',
        }}>
          <div style={{
            background: 'white', borderRadius: '16px', padding: '32px',
            width: '100%', maxWidth: '540px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            maxHeight: '90vh', overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '800', color: '#0F172A', margin: 0 }}>Edit Customer</h2>
              <div onClick={() => { setShowEditModal(false); resetForm() }} style={{
                width: '32px', height: '32px', borderRadius: '8px', background: '#F1F5F9',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', fontSize: '16px', color: '#64748B',
              }}>×</div>
            </div>
            {formJsx}
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button onClick={() => { setShowEditModal(false); resetForm() }} style={{
                flex: 1, height: '44px', borderRadius: '10px',
                background: 'white', border: '1.5px solid #E2E8F0',
                fontSize: '14px', fontWeight: '600', color: '#64748B', cursor: 'pointer',
              }}>Cancel</button>
              <button onClick={handleEdit} disabled={saving} style={{
                flex: 1, height: '44px', borderRadius: '10px',
                background: 'linear-gradient(135deg, #2563EB, #1D4ED8)',
                color: 'white', fontSize: '14px', fontWeight: '700',
                border: 'none', cursor: saving ? 'not-allowed' : 'pointer',
              }}>{saving ? 'Saving...' : 'Save Changes'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── View Customer Modal ── */}
      {showViewModal && selectedCustomer && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 100, padding: '24px',
        }}>
          <div style={{
            background: 'white', borderRadius: '16px', padding: '32px',
            width: '100%', maxWidth: '540px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            maxHeight: '90vh', overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '800', color: '#0F172A', margin: 0 }}>Customer Details</h2>
              <div onClick={() => setShowViewModal(false)} style={{
                width: '32px', height: '32px', borderRadius: '8px', background: '#F1F5F9',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', fontSize: '16px', color: '#64748B',
              }}>×</div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={typeBadge(selectedCustomer.customer_type)}>
                  {selectedCustomer.customer_type || 'Business'}
                </span>
              </div>

              {[
                { label: 'Name', value: selectedCustomer.customer_name },
                { label: 'Email', value: selectedCustomer.email || '—' },
                { label: 'Phone', value: selectedCustomer.phone || '—' },
                { label: 'Billing Address', value: selectedCustomer.address || '—' },
              ].map((item, i) => (
                <div key={i} style={{ padding: '12px 16px', borderRadius: '8px', background: '#F8FAFC', border: '1px solid #EAECF0' }}>
                  <p style={{ fontSize: '11px', fontWeight: '600', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 4px' }}>{item.label}</p>
                  <p style={{ fontSize: '14px', fontWeight: '500', color: '#0F172A', margin: 0 }}>{item.value}</p>
                </div>
              ))}

              {selectedCustomer.customer_type === 'Business' && (
                <>
                  {selectedCustomer.vat_number && (
                    <div style={{ padding: '12px 16px', borderRadius: '8px', background: '#F8FAFC', border: '1px solid #EAECF0' }}>
                      <p style={{ fontSize: '11px', fontWeight: '600', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 4px' }}>VAT Number</p>
                      <p style={{ fontSize: '14px', fontWeight: '500', color: '#0F172A', margin: 0 }}>{selectedCustomer.vat_number}</p>
                    </div>
                  )}
                  {selectedCustomer.site_addresses?.length > 0 && (
                    <div style={{ padding: '12px 16px', borderRadius: '8px', background: '#F8FAFC', border: '1px solid #EAECF0' }}>
                      <p style={{ fontSize: '11px', fontWeight: '600', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 8px' }}>
                        Site Addresses ({selectedCustomer.site_addresses.length})
                      </p>
                      {selectedCustomer.site_addresses.map((addr, i) => (
                        <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: i < selectedCustomer.site_addresses.length - 1 ? '8px' : 0 }}>
                          <div style={{
                            width: '20px', height: '20px', borderRadius: '50%',
                            background: '#EFF6FF', display: 'flex', alignItems: 'center',
                            justifyContent: 'center', fontSize: '10px', fontWeight: '700',
                            color: '#2563EB', flexShrink: 0, marginTop: '1px',
                          }}>{i + 1}</div>
                          <p style={{ fontSize: '13px', color: '#0F172A', margin: 0 }}>{addr}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              <div style={{ padding: '12px 16px', borderRadius: '8px', background: '#F8FAFC', border: '1px solid #EAECF0' }}>
                <p style={{ fontSize: '11px', fontWeight: '600', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 4px' }}>Added</p>
                <p style={{ fontSize: '14px', fontWeight: '500', color: '#0F172A', margin: 0 }}>
                  {new Date(selectedCustomer.created_at).toLocaleDateString('en-GB')}
                </p>
              </div>
            </div>

            <button onClick={() => setShowViewModal(false)} style={{
              width: '100%', height: '44px', borderRadius: '10px', marginTop: '24px',
              background: 'white', border: '1.5px solid #E2E8F0',
              fontSize: '14px', fontWeight: '600', color: '#64748B', cursor: 'pointer',
            }}>Close</button>
          </div>
        </div>
      )}
    </div>
  )
}